import { Typography } from '@components/typography';
import { Organization } from '@generated/graphql';
import { calculateDateDifference } from '@helper-functions/calculateDateDifference';
import { useFeatureFlag } from '@hooks/FeatureFlags';
import { Box, Divider, Tab, Tabs, useTheme } from '@mui/material';
import { LandingPageNavigationTab } from '@subsets/landing-page';
import { navigate } from 'gatsby';

const isOrganizationExpired = (organizations: Organization[], selectedOrganizationId: string) => {
  const organization = organizations.find(
    ({ organizationId }) => organizationId === selectedOrganizationId,
  );
  const { hours } = calculateDateDifference(organization?.expirationDate);
  return organization.expired || hours < 1;
};

type LandingPageNavigationProps = {
  organizations: Organization[];
  tab: string;
};

export const LandingPageNavigation = ({ organizations, tab }: LandingPageNavigationProps) => {
  const { palette } = useTheme();

  const showRemoteSessionsTab = useFeatureFlag('enable-remote-sessions-on-landing');

  const handleChange = (_, val: string) => {
    const orgTab = new URLSearchParams(window.location.search).get('tab');
    void navigate(`/${val}${orgTab ? `?tab=${orgTab}` : ''}`);
  };

  return (
    <Box
      width="100%"
      height="100%"
      overflow="auto"
      sx={{ overflowX: 'hidden', paddingTop: 6, backgroundColor: palette.background.paper }}
    >
      <Tabs
        orientation="vertical"
        value={tab}
        onChange={handleChange}
        sx={{
          '& .MuiTabs-indicator': { backgroundColor: 'transparent' },
          '& .MuiTabs-fixed': { borderBottom: 'none' },
        }}
      >
        <LandingPageNavigationTab value="recent-workspaces" label="Recent workspaces" id="recent" />
        {showRemoteSessionsTab && (
          <LandingPageNavigationTab
            value="remote-sessions"
            label="Remote sessions"
            id="remote-sessions"
          />
        )}
        <LandingPageNavigationTab
          value="sample-gallery"
          label="Sample gallery"
          id="sample-gallery"
        />
        <Tab
          label=""
          sx={{ p: 0 }}
          icon={
            <Divider
              sx={{
                marginTop: 4,
                marginBottom: 4,
                borderColor: palette.background.default,
                borderTopWidth: '1px',
                width: '100%',
              }}
            />
          }
          disabled
        />
        {organizations.map(({ name, organizationId }: Organization) => (
          <LandingPageNavigationTab
            label={
              <Typography
                variant="body2"
                handleOverFlow
                noWrap
                paddingRight="16px"
                textAlign="left"
              >
                {name}
              </Typography>
            }
            expired={isOrganizationExpired(organizations, organizationId)}
            value={organizationId}
            key={organizationId}
          />
        ))}
      </Tabs>
    </Box>
  );
};
